import { Component, OnDestroy, OnInit, SimpleChanges, inject } from "@angular/core";
import { PiensoApp } from "./interfaces";
import { LinkVO, FULL_NAVIGATION, CHILD_ROUTES, CategoryHash, CategoryVO } from "./topbar.config";
import UrlPattern from "url-pattern";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { KeycloakProfile } from "keycloak-js";
import { firstValueFrom, Subject, takeUntil } from "rxjs";
import { NavigationConfigService } from "./navigation-config.service";
import { compareArrays } from "../shared/array-utils";
import { NgClass } from "@angular/common";
import {
  NgbTooltip,
  NgbDropdown,
  NgbDropdownToggle,
  NgbDropdownMenu,
  NgbDropdownItem,
} from "@ng-bootstrap/ng-bootstrap";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import Keycloak from "keycloak-js";

@Component({
  selector: "pienso-topbar",
  templateUrl: "./topbar.component.html",
  styleUrls: ["./topbar.component.scss"],
  imports: [NgbTooltip, NgbDropdown, NgClass, NgbDropdownToggle, FaIconComponent, NgbDropdownMenu, NgbDropdownItem],
})
export class TopbarComponent implements OnInit, OnDestroy {
  private readonly keycloak = inject(Keycloak);
  private configService = inject(NavigationConfigService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  piensoApps: PiensoApp[] = ["INGEST", "CORE", "ANALYSIS", "EXPLORE", "MONITOR"];

  public profile: KeycloakProfile | undefined;

  public topbarConfig: CategoryHash = {};

  // @ts-ignore
  public activeCategory: LinkVO = {};

  // @ts-ignore
  public activeLink: LinkVO | undefined = {};

  private destroy$ = new Subject<void>();

  async ngOnInit() {
    this.profile = await this.keycloak.loadUserProfile();
    this.piensoApps = await firstValueFrom(this.configService.getPiensoApps());
    this.buildConfig();
    // this.onRouteUpdate(this.route.toString());
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe({
      next: (evt) => {
        if (evt instanceof NavigationEnd) {
          this.onRouteUpdate(evt.urlAfterRedirects);
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  onRouteUpdate(url: string) {
    this.getAllLinks().forEach((link) => (link.selected = false));
    this.getAllCategories().forEach((link) => (link.selected = false));

    if (url.includes("?")) {
      url = url.split("?")[0];
    }
    // url = url.toLowerCase();
    if (url.charAt(0) === "/") {
      url = url.substring(1);
    }

    // map the url to an app:
    const path = url.split("/");
    const app = path[0].toUpperCase() as PiensoApp;
    const appPrefix = this.getAppRoot(app);
    const route = url.substring(appPrefix.length + 1);
    const link = (this.activeLink = this.getLinkByRoute(app, route));
    if (link) {
      const category = this.getCategoryByChildLink(link);
      if (category) {
        this.activeCategory = category;
        category.selected = true;
      }
    } else {
      // select the app
      const configApp = this.topbarConfig[app];
      if (configApp) {
        configApp.display.selected = true;
      }
    }
  }

  /** Returns the top level sections in order */
  getSections(): CategoryVO[] {
    const sections: CategoryVO[] = [];
    for (const key of ["HOME", "INGEST", "MODEL", "ANALYSIS", "DEPLOY"]) {
      if (this.topbarConfig[key]) {
        sections.push(this.topbarConfig[key]);
      }
    }
    return sections;
  }

  getCategoryName(section: CategoryVO): string {
    const matchName: boolean = section.display.name === this.activeCategory.name;
    const canDropDown: boolean = this.canDropDown(section.children).length > 0;
    if (matchName && canDropDown && this.activeLink) {
      return `${section.display.name} : ${this.activeLink.name}`;
    } else {
      return section.display.name;
    }
  }

  canDropDown(children: LinkVO[]): LinkVO[] {
    return children.filter((child) => child.hideFromMenu !== true);
  }

  gotoURL(item: LinkVO): void {
    if (item.active) {
      let { app, route } = item;
      route = route || [];
      this.router.navigate([this.getAppRoot(app), ...route]);
    }
  }

  onLogout(): void {
    let url = window.location.origin;
    url += "/auth/realms/Pienso/protocol/openid-connect/logout?post_logout_redirect_uri=";
    url += encodeURIComponent(window.location.origin);
    url += "&client_id=angular-web";
    window.location.assign(url);
  }

  private getAppRoot(app: PiensoApp): string {
    switch (app) {
      case "INGEST":
        return "ingest";
      case "ANALYSIS":
        return "analysis";
      case "EXPLORE":
        return "explore";
      case "MONITOR":
        return "monitor";
      case "CORE":
      default:
        return "core";
    }
  }

  private getCategoryByChildLink(child: LinkVO): LinkVO | void {
    const keys: string[] = Object.keys(this.topbarConfig);
    for (const key of keys) {
      const match: LinkVO | void = this.getLinkByChildLink(this.topbarConfig[key].children, child);
      if (match) {
        return this.topbarConfig[key].display;
      }
    }
  }

  private getLinkByChildLink(links: LinkVO[], child: LinkVO): LinkVO | void {
    const match: LinkVO | undefined = links.find((alink) => alink.app === child.app && alink.name === child.name);
    if (match) {
      return match;
    }
    for (const link of links) {
      const match = this.getLinkByChildLink(link.children, child);
      if (match) {
        return match;
      }
    }
  }

  private getLinkByRoute(app: string, route: string): LinkVO | undefined {
    const configuredLinks: LinkVO[] = this.getAllLinks();
    return configuredLinks.find((linkvo) => {
      if (linkvo.app === app) {
        if (linkvo.patterns) {
          return linkvo.patterns.some((spattern) => !!new UrlPattern(spattern).match(route));
        } else if (compareArrays(linkvo.route, route.split("/"))) {
          return true;
        }
      }
      return false;
    });
  }

  private getAllCategories(): LinkVO[] {
    const parents = Object.keys(this.topbarConfig).map((key) => this.topbarConfig[key]);
    const links: LinkVO[] = parents.map((value) => value.display);
    return links;
  }

  private getAllLinks(): LinkVO[] {
    const parents = Object.keys(this.topbarConfig).map((key) => this.topbarConfig[key]);
    const links: LinkVO[] = ([] as LinkVO[]).concat.apply(
      [],
      parents.map((category) => this.getLowerLevelLinks(category.children)),
    );
    return links;
  }

  private getLowerLevelLinks(links: LinkVO[]): LinkVO[] {
    const lowerLinks: LinkVO[] = ([] as LinkVO[]).concat.apply(
      [],
      links.map((link) => this.getLowerLevelLinks(link.children)),
    );
    return ([] as LinkVO[]).concat(links, lowerLinks);
  }

  private buildConfig(): void {
    const config: CategoryHash = JSON.parse(JSON.stringify(FULL_NAVIGATION)); // Deep Clone

    const hasAnalysis = this.piensoApps.indexOf("ANALYSIS") >= 0;
    const hasExplore = this.piensoApps.indexOf("EXPLORE") >= 0;
    const hasMonitor = this.piensoApps.indexOf("MONITOR") >= 0;

    config["HOME"].children.push(CHILD_ROUTES.HOME);

    const configLibrary = JSON.parse(JSON.stringify(CHILD_ROUTES.LIBRARY));
    configLibrary.children.push(CHILD_ROUTES.LIBRARY_MODELS, CHILD_ROUTES.STOPWORDS);
    config["MODEL"].children.push(
      CHILD_ROUTES.TRAINING_DATA,
      configLibrary,
      CHILD_ROUTES.DEEP_LEARNING,
      CHILD_ROUTES.TRAINING_WORKSPACE,
      CHILD_ROUTES.DEEP_LEARNING_WORKSPACE,
    );

    config["INGEST"].children.push(CHILD_ROUTES.DATA_LIBRARY);
    config["DEPLOY"].children.push(CHILD_ROUTES.DEPLOY_MODELS);
    if (hasExplore) {
      const exploreConfig = JSON.parse(JSON.stringify(CHILD_ROUTES.EXPLORE));
      exploreConfig.children.push(CHILD_ROUTES.EXPLORATIONS, CHILD_ROUTES.DATA_SETS);
      config["ANALYSIS"].children.push(exploreConfig);
    }

    if (hasMonitor) {
      config["ANALYSIS"].children.push(CHILD_ROUTES.MONITOR);
    }

    if (hasAnalysis) {
      config["ANALYSIS"].children.push(CHILD_ROUTES.VIEW_DASHBOARDS, CHILD_ROUTES.UNIT_LIST);
    }

    if (!hasAnalysis && !hasExplore) {
      delete config["ANALYSIS"];
    }

    this.topbarConfig = config;
  }

  hasRoleAccess(child: LinkVO): boolean {
    if (!child.hasOwnProperty("role")) return false;

    

    const parsed = this.keycloak.tokenParsed as any;
    return parsed.realm_access?.roles?.includes(child.role);
  }

  hasChildWithRoleAccess(section: CategoryVO): boolean {
    for (const child of section.children) {
      if (this.hasRoleAccess(child)) return true;
    }
    return false;
  }
}
